import { Component, OnInit, ChangeDetectorRef, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatRadioModule } from '@angular/material/radio';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCardModule } from '@angular/material/card';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatRadioGroup, MatRadioButton} from '@angular/material/radio';
import { Router } from '@angular/router';
import { NearMissService } from '../../../../services/nearMiss/nearMiss.service';
import { Nearmiss } from '../../../../services/nearMiss/nearMiss.interface';
import { SubmitDiagDirective } from '../../../../services/diagnostics/submit-diag.directive';
import { CommonFunctionsService } from '../../../../services/common-functions.service';

@Component({
    selector: 'app-near-miss',
    imports: [
    SubmitDiagDirective,
    MatButtonModule,
    MatCheckboxModule,
    MatInputModule,
    MatSelectModule,
    MatRadioModule,
    MatFormFieldModule,
    MatCardModule,
    FormsModule,
    ReactiveFormsModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MatRadioButton,
    MatRadioGroup
],
    templateUrl: './near-miss.component.html',
    styleUrl: './near-miss.component.css'
})
export class NearMissComponent implements OnInit {
  isSubmitting = false;
  warningMessage = '';

  // sessionStorage draft key — see saveDraft/restoreDraft below.
  private readonly draftKey = 'nearmiss-draft';

  readonly offices = [
    { value: 'ftworth', label: 'Fort Worth' },
    { value: 'houston', label: 'Houston' },
    { value: 'irving', label: 'Irving' },
    { value: 'windsor', label: 'Windsor' },
  ];

  form: FormGroup
  constructor(private router: Router, private fb: FormBuilder, private nearMissService: NearMissService, private cdr: ChangeDetectorRef, private destroyRef: DestroyRef, private commonFuncs: CommonFunctionsService ) {
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      dateOfReport: [null, [Validators.required]],
      supervisor: [null, [Validators.required]],
      office: [null, [Validators.required]],
      nearMissDate: [null, [Validators.required]],
      time: [null, [Validators.required]],
      location: [null, [Validators.required]],
      type: [null, [Validators.required]],
      equipment: [null, [Validators.required]],
      persons: [null],
      desc: [null, [Validators.required]],
      rate: [null, [Validators.required]],
      actions: [null, [Validators.required]],
      dateDiscussed: [null, [Validators.required]],
      completedBy:[null],
      title: [null],
      email: [null],
    })

    // Restore a draft left behind by a prior submit that was blocked for re-auth.
    this.restoreDraft();
  }

  create(): void {
    if (this.isSubmitting) return;

    if (this.form.invalid) {
      return;
    }

    this.isSubmitting = true;
    this.warningMessage = '';

    // Persist BEFORE the request so the draft survives an auth-recovery redirect/refresh.
    this.saveDraft();

    const newNearMiss: Nearmiss = {
      dateOfReport: this.form.controls['dateOfReport'].value as Date,
      supervisor: this.form.controls['supervisor'].value as string,
      office: this.form.controls['office'].value as string,
      nearMissDate: this.form.controls['nearMissDate'].value as Date,
      time: this.commonFuncs.formatTimeOfDay(this.form.controls['time'].value as Date),
      location: this.form.controls['location'].value as string,
      type: this.form.controls['type'].value as string,
      equipment: this.form.controls['equipment'].value as string,
      persons: this.form.controls['persons'].value as string,
      desc: this.form.controls['desc'].value as string,
      rate: this.form.controls['rate'].value as string,
      actions:this.form.controls['actions'].value as string,
      dateDiscussed: this.form.controls['dateDiscussed'].value as Date,
      completedBy:this.form.controls['completedBy'].value as string,
      title: this.form.controls['title'].value as string,
      email: this.form.controls['email'].value as string,
    };
    this.nearMissService.createNearMiss(newNearMiss)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.clearDraft();
          this.router.navigate(['success']);
          this.isSubmitting = false;
        },
        error: (err: HttpErrorResponse) => {
          console.error('Error, submission failed', err);
          this.isSubmitting = false;
          this.warningMessage = this.submissionErrorMessage(err);
          this.cdr.detectChanges();
        },
      });
  }

  private submissionErrorMessage(err: HttpErrorResponse): string {
    const preserved = 'Your form data has been preserved.';
    if (err?.status === 401 || err?.status === 403) {
      return `Your session has expired or you've been signed out. Please refresh the page to sign back in, then resubmit. ${preserved}`;
    }
    if (err?.status === 0) {
      return `Submission failed — we couldn't reach the server. Please check your connection and try again. ${preserved}`;
    }
    return `Submission failed. Please try again in a moment. ${preserved}`;
  }

  // --- Draft persistence (sessionStorage) --------------------------------------------
  // Best-effort: storage can be unavailable (private mode / quota) — never let it break a
  // submit or a page load.

  private saveDraft(): void {
    try {
      sessionStorage.setItem(this.draftKey, JSON.stringify(this.form.getRawValue()));
    } catch {
      // storage unavailable — submit proceeds normally.
    }
  }

  private clearDraft(): void {
    try {
      sessionStorage.removeItem(this.draftKey);
    } catch {
      // ignore
    }
  }

  private restoreDraft(): void {
    let raw: string | null = null;
    try {
      raw = sessionStorage.getItem(this.draftKey);
    } catch {
      return;
    }
    if (!raw) return;

    try {
      const draft = JSON.parse(raw);
      // Date controls serialize to ISO strings; revive them for the Material datepicker.
      if (draft.dateOfReport) draft.dateOfReport = new Date(draft.dateOfReport);
      if (draft.nearMissDate) draft.nearMissDate = new Date(draft.nearMissDate);
      if (draft.dateDiscussed) draft.dateDiscussed = new Date(draft.dateDiscussed);
      if (draft.time) draft.time = new Date(draft.time);
      this.form.patchValue(draft);
      this.warningMessage =
        'We restored your unsaved near-miss report from a previous attempt. Please review the entries, then resubmit.';
      this.cdr.detectChanges();
    } catch {
      this.clearDraft();
    }
  }

}
